import { Answer, QuestionInPage, QuestionnairePage } from 'src/app/core/models/questionnaire.model';
import { isEffectivelyElective } from './elective';
import { runConditions } from './hideif';

const NON_ANSWERABLE_RESPONSE_TYPES = new Set(['EXPLANATION', 'LINK']);

export function isRequiredAnswerPresent(answer: Answer | undefined): boolean {
  if (!answer) return false;
  const hasValue = answer.value !== null && answer.value !== undefined && answer.value !== '';
  const info = answer.addtional_information;
  const hasInfo =
    info !== null &&
    info !== undefined &&
    !(Array.isArray(info) && info.length === 0) &&
    !(typeof info === 'object' && !Array.isArray(info) && Object.keys(info).length === 0);
  return hasValue || hasInfo;
}

type PageForCheck = Pick<QuestionnairePage, 'page_config' | 'cp_question_in_page'>;

function pageHasUnansweredQuestion(
  page: PageForCheck,
  data: { answers: Record<string, Answer> },
  countsQuestion: (question: QuestionInPage['cp_question']) => boolean
): boolean {
  const skipIf = page.page_config?.skip_if;
  if (skipIf && runConditions(skipIf, data)) return false;

  return page.cp_question_in_page.some((qip) => {
    const question = qip.cp_question;
    if (NON_ANSWERABLE_RESPONSE_TYPES.has(question.response_type)) return false;

    const hideIf = qip.question_logic?.hide_if;
    if (hideIf && runConditions(hideIf, data)) return false;

    if (!countsQuestion(question)) return false;

    return !isRequiredAnswerPresent(data.answers?.[question.slug]);
  });
}

export function pageHasPendingRequiredQuestion(
  page: PageForCheck,
  caseAllowsSimpleQuestionnaire: boolean,
  data: { answers: Record<string, Answer> }
): boolean {
  return pageHasUnansweredQuestion(
    page,
    data,
    (question) => !isEffectivelyElective(question, caseAllowsSimpleQuestionnaire)
  );
}

/**
 * CPK-4899: same rules as pageHasPendingRequiredQuestion, but elective and
 * skippable questions count too — an agent resuming an OPEN answer lands on
 * whatever is still blank, not only on what blocks the submit.
 */
export function pageHasBlankQuestion(
  page: PageForCheck,
  data: { answers: Record<string, Answer> }
): boolean {
  return pageHasUnansweredQuestion(page, data, () => true);
}

export function findFirstPendingRequiredPageIndex(
  pages: Pick<QuestionnairePage, 'page_config' | 'cp_question_in_page'>[],
  caseAllowsSimpleQuestionnaire: boolean,
  data: { answers: Record<string, Answer> }
): number | null {
  for (let i = 0; i < pages.length; i++) {
    if (pageHasPendingRequiredQuestion(pages[i], caseAllowsSimpleQuestionnaire, data)) return i;
  }
  return null;
}

export function hasPendingRequiredQuestion(
  pages: Pick<QuestionnairePage, 'page_config' | 'cp_question_in_page'>[],
  caseAllowsSimpleQuestionnaire: boolean,
  data: { answers: Record<string, Answer> }
): boolean {
  return findFirstPendingRequiredPageIndex(pages, caseAllowsSimpleQuestionnaire, data) !== null;
}

export function findFirstBlankPageIndex(
  pages: PageForCheck[],
  data: { answers: Record<string, Answer> }
): number | null {
  for (let i = 0; i < pages.length; i++) {
    if (pageHasBlankQuestion(pages[i], data)) return i;
  }
  return null;
}
